import React from "react";
import { IDropdownOption, Icon, TooltipHost } from "@fluentui/react";
import { ExcelLogoIcon } from "@fluentui/react-icons";
import { ThemeContext } from '@fluentui/react-theme-provider';
import DocumentTitle from "react-document-title";
import { ReactComponent as IncrementIcon } from '../../images/increment.svg';
import { ReactComponent as DecrementIcon } from '../../images/decrement.svg';
import { ActionButton, Card, CardData, CardsContainer, CardTitle, Container, GraphicsContainer, GraphicTitle, HeaderContainer, Label, MetricCardData, MetricsContainer, StyledDropdown, CounterLabel, DateNavigator, NavigationArrow, DateLabel, EditButton, DeleteButton, MainButton, ExportIcon, GraphicsContentContainer, RatiosSectionContainer, ChartSectionContainer } from "./SummaryPortfolioModel/Styles/SummaryPortfolioModelStyles";
import Highstock, { GraphicType, CustomRangeButton } from "../../components/Highstock/Highstock";
import PortfolioModelTable from "./SummaryPortfolioModel/Components/PortfolioModelTable";
import portfolioModelWallets from "../../services/PortfolioModelWalletService";
import { useLocation, useHistory } from 'react-router-dom';
import FundService, { IPortfolioModelFullInformation } from "../../services/FundService";
import { IPortfolioModelHistoric, IFundData, DataAndCartLines, CartLinesFromDB, IDataRatios } from "../../services/PortfolioModelWalletService";
import ImportComposition from "./SummaryPortfolioModel/Components/ImportComposition";
import ManageComposition from "./SummaryPortfolioModel/Components/ManageComposition";
import DeleteCompositionModal from "./SummaryPortfolioModel/Components/DeleteCompositionModal";
import LoadingGraphicComponent from "./SummaryPortfolioModel/Components/LoadingGraphicComponent";
import RatioTablePortfolioModel from "./SummaryPortfolioModel/Components/RatioTablePortfolioModel";
import Auth from "../../Auth/Auth";
import { CachedPortfolioData, IPortfolioModelTable, LocationState, SerieData } from "./SummaryPortfolioModel/Interfaces/SummaryPortfolioInterfaces";
import { formatPortfolioModelDate } from "./SummaryPortfolioModel/Functions/FormatPortfolioModelDate";
import { convertPortfolioDataToFundData } from "./SummaryPortfolioModel/Functions/ConvertPortfolioDataToFundData";
import { wrapWithMetadata, unwrapMetadata } from "./SummaryPortfolioModel/Utils/CacheUtils";

// Usamos React.memo para evitar re-renders innecesarios en el componente Highstock
// Así sólo lo repintamos cuando sea necesario y la página va mucho más fluida.
const MemoizedHighstock = React.memo(Highstock);

const SummaryPortfolioModel = () =>
{
    const theme = React.useContext(ThemeContext);
    const location = useLocation<LocationState>();
    const history = useHistory();
    const customOptions = React.useMemo(() => ({}), []);
    const [selectedOptions, setSelectedOptions] = React.useState<string[]>(location.state?.portfolioModelId ? [location.state.portfolioModelId.toString()] : []);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isInterfaceDisabled, setIsInterfaceDisabled] = React.useState(false);
    const [series, setSeries] = React.useState<any[]>([]);
    const [modelPortfolioComposition, setModelPortfolioComposition] = React.useState<IPortfolioModelTable[]>([]);
    const [noDataAvailable, setNoDataAvailable] = React.useState<boolean>(false);
    const [noChartDataOnly, setNoChartDataOnly] = React.useState<boolean>(false);
    const [availableDates, setAvailableDates] = React.useState<string[]>([]);
    const [counterValue, setCounterValue] = React.useState<number>(0);
    const [currentDateIndex, setCurrentDateIndex] = React.useState<number>(0);
    const [isLoadingCompositions, setIsLoadingCompositions] = React.useState<boolean>(false);
    const [isImportModalOpen, setIsImportModalOpen] = React.useState<boolean>(false);
    const [isManageCompositionModalOpen, setIsManageCompositionModalOpen] = React.useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
    const [isDeletingComposition, setIsDeletingComposition] = React.useState<boolean>(false);
    const [ratiosData, setRatiosData] = React.useState<IDataRatios | null>(null);
    const [selectedPeriod, setSelectedPeriod] = React.useState<string>('inception');
    
    // Calcular el número máximo de puntos de datos en las series
    const getMaxDataPoints = React.useMemo(() =>
    {
        if(!series || !Array.isArray(series) || series.length === 0)
        {
            return 0;
        }
        
        let maxPoints = 0;
        series.forEach((s: any) =>
        {
            if(s.data && Array.isArray(s.data))
            {
                maxPoints = Math.max(maxPoints, s.data.length);
            }
        });
        
        return maxPoints;
    }, [series]);

    // Mapeo de índices de botones a períodos
    // Este array se construye dinámicamente según los botones que se van a renderizar
    const buttonIndexToPeriod = React.useMemo(() =>
    {
        // Orden base: 1m, 3m, 6m, 9m, 1y (siempre presentes)
        const periods = ['1m', '3m', '6m', '9m', '1y'];
        
        // Agregar botones condicionales solo si hay datos completos del período
        // 2y: requiere exactamente 730 puntos (2 años completos)
        if(getMaxDataPoints >= 730)
        {
            periods.push('2y');
        }
        
        // 3y: requiere exactamente 1095 puntos (3 años completos)
        if(getMaxDataPoints >= 1095)
        {
            periods.push('3y');
        }
        
        // Siempre al final: YTD y Todo
        periods.push('ytd', 'inception');
        
        return periods;
    }, [getMaxDataPoints]);
    
    // Función para manejar el cambio de período desde los botones de Highstock
    const handlePeriodChange = React.useCallback((buttonIndex: number) =>
    {
        // Usar el array de mapeo de índices a períodos
        const newPeriod = buttonIndexToPeriod[buttonIndex];
        if(newPeriod)
        {
            setSelectedPeriod(newPeriod);
        }
    }, [buttonIndexToPeriod]);
    const [isManagingComposition, setIsManagingComposition] = React.useState<boolean>(false);
    const [manageCompositionMode, setManageCompositionMode] = React.useState<'add' | 'edit'>('add');
    // Estado para almacenar los datos de composiciones históricas
    const [compositionsData, setCompositionsData] = React.useState<IPortfolioModelHistoric[]>([]);
    // Estado para indicar que se están cargando datos de una composición específica
    const [isLoadingCompositionData, setIsLoadingCompositionData] = React.useState<boolean>(false);
    // Semáforo para controlar el refresco después de modificaciones
    const [mustRefresh, setMustRefresh] = React.useState<boolean>(false);
    var userId = Auth.getUserProfile().id;
    
    // Guardamos para evitar los re-renders innecesarios
    const loadedPortfolioIdsRef = React.useRef<Set<string>>(new Set());
    const loadedCompositionsRef = React.useRef<Set<number>>(new Set());
    const modelPortfolioCompositionRef = React.useRef<IPortfolioModelTable[]>([]);
    const currentlyLoadingCompositionsRef = React.useRef<Set<string>>(new Set());

    // Mantener actualizada la referencia para evitar dependencias problemáticas
    React.useEffect(() =>
    {
        modelPortfolioCompositionRef.current = modelPortfolioComposition;
    }, [modelPortfolioComposition]);

    const options: IDropdownOption[] = React.useMemo(() => location.state?.portfolioModels ? location.state.portfolioModels.map(model =>
    ({
        key: model.id.toString(),
        text: model.name
    })) : [], [location.state?.portfolioModels]);

    // Memoizamos el portfolioModelName para evitar cálculos repetidos
    const portfolioModelName = React.useMemo(() => 
    {
        return selectedOptions.length === 1 ? options.find(opt => opt.key === selectedOptions[0])?.text : undefined;
    }, [selectedOptions, options]);

    //Zona de cacheo de sessionStorage

    const getStorageKey = (portfolioModelId: string) => `portfolioModel_${portfolioModelId}`;
    const getChartStorageKey = (portfolioModelId: string) => `portfolioModelChart_${portfolioModelId}`;
    const getRatiosStorageKey = (portfolioModelId: string) => `portfolioModelRatios_${portfolioModelId}`;
    const getCompositionStorageKey = (portfolioModelId: string, compositionId: number) => `composition_${portfolioModelId}_${compositionId}`;

    const saveToSessionStorage = React.useCallback((portfolioModelId: string, data: CachedPortfolioData) =>
    {   
        try
        {
            sessionStorage.setItem(getStorageKey(portfolioModelId), wrapWithMetadata(data.tableData));
            sessionStorage.setItem(getChartStorageKey(portfolioModelId), wrapWithMetadata(data.chartData));
            if(data.ratiosData)
            {
                sessionStorage.setItem(getRatiosStorageKey(portfolioModelId), wrapWithMetadata(data.ratiosData));
            }
        }
        catch(error)
        {
        }
    }, []);

    const getFromSessionStorage = React.useCallback((portfolioModelId: string): CachedPortfolioData | null =>
    {
        try
        {
            const tableDataString = sessionStorage.getItem(getStorageKey(portfolioModelId));
            const chartDataString = sessionStorage.getItem(getChartStorageKey(portfolioModelId));
            const ratiosDataString = sessionStorage.getItem(getRatiosStorageKey(portfolioModelId));

            if(!tableDataString || !chartDataString)
            {
                return null;
            }

            // Validar la fecha de caché
            const parsedTableData = unwrapMetadata<IPortfolioModelTable[]>(tableDataString);
            const parsedChartData = unwrapMetadata<any[]>(chartDataString);
            const parsedRatiosData = ratiosDataString ? unwrapMetadata<IDataRatios>(ratiosDataString) : null;

            // Si alguno de los datos críticos está expirado o es inválido, limpiar toda la caché
            if(!parsedTableData || !parsedChartData)
            {
                sessionStorage.removeItem(getStorageKey(portfolioModelId));
                sessionStorage.removeItem(getChartStorageKey(portfolioModelId));
                sessionStorage.removeItem(getRatiosStorageKey(portfolioModelId));
                return null;
            }

            if(Array.isArray(parsedTableData) && parsedTableData.length > 0)
            {
                const expectedModelId = parseInt(portfolioModelId);
                const inconsistentData = parsedTableData.some((item: IPortfolioModelTable) => item.modelPortfolioId && item.modelPortfolioId !== expectedModelId);

                if(inconsistentData)
                {
                    sessionStorage.removeItem(getStorageKey(portfolioModelId));
                    sessionStorage.removeItem(getChartStorageKey(portfolioModelId));
                    sessionStorage.removeItem(getRatiosStorageKey(portfolioModelId));
                    return null;
                }
            }

            return{
                tableData: parsedTableData,
                chartData: parsedChartData,
                isNoChartDataOnly: false,
                ratiosData: parsedRatiosData
            };
        }
        catch(error)
        {
            return null;
        }
    }, []);

    // Funciones de caché para composiciones
    const saveCompositionToSessionStorage = React.useCallback((portfolioModelId: string, compositionId: number, compositionData: IPortfolioModelTable[]) =>
    {
        try
        {
            const storageKey = getCompositionStorageKey(portfolioModelId, compositionId);
            sessionStorage.setItem(storageKey, wrapWithMetadata(compositionData));
        }
        catch(error)
        {
        }
    }, []);

    const getCompositionFromSessionStorage = React.useCallback((portfolioModelId: string, compositionId: number): IPortfolioModelTable[] | null =>
    {
        try
        {
            const storageKey = getCompositionStorageKey(portfolioModelId, compositionId);
            const cachedDataString = sessionStorage.getItem(storageKey);

            if(!cachedDataString) 
            {
                return null;
            }

            // Validar la fecha de caché
            const parsedData = unwrapMetadata<IPortfolioModelTable[]>(cachedDataString);

            // Si la caché está expirada o es inválida
            if(!parsedData)
            {
                sessionStorage.removeItem(storageKey);
                return null;
            }

            // Verificar que los datos no estén corruptos
            if(!Array.isArray(parsedData))
            {
                sessionStorage.removeItem(storageKey);
                return null;
            }

            return parsedData;
        }
        catch(error)
        {
            return null;
        }
    }, []);

    // Función para limpiar el caché de una composición específica al editarla
    const clearCompositionFromSessionStorage = React.useCallback((portfolioModelId: string, compositionId: number) =>
    {
        try
        {
            const storageKey = getCompositionStorageKey(portfolioModelId, compositionId);
            sessionStorage.removeItem(storageKey);
        }
        catch(error)
        {
        }
    }, []);

    // Función para cargar datos de una composición específica
    const loadCompositionData = React.useCallback(async (portfolioModelId: string, compositionId: number): Promise<IPortfolioModelTable[]> =>
    {
        // Si es la composición virtual (compositionId = -1), usar los datos actuales de la cartera modelo
        if(compositionId === -1)
        {
            // Si mustRefresh está activo, tenemos que limpiar caché para forzar recarga
            if(mustRefresh)
            {
                sessionStorage.removeItem(getStorageKey(portfolioModelId));
                sessionStorage.removeItem(getChartStorageKey(portfolioModelId));
                sessionStorage.removeItem(getRatiosStorageKey(portfolioModelId));
                
                // Si mustRefresh está activo, no devolver datos vacíos, sino esperar a que se recarguen
                return [];
            }

            const cachedPortfolioData = getFromSessionStorage(portfolioModelId);
            return cachedPortfolioData?.tableData || [];
        }

        // Verificar caché primero
        const cachedCompositionData = getCompositionFromSessionStorage(portfolioModelId, compositionId);
        if(cachedCompositionData)
        {
            return cachedCompositionData;
        }

        // Si no está en caché, buscar en los datos ya cargados de composiciones
        const existingComposition = compositionsData.find(comp => comp.compositionId === compositionId);

        if(existingComposition && existingComposition.fundsData && existingComposition.fundsData.length > 0)
        {
            // Los datos ya están disponibles, pero necesitamos obtener información completa de los fondos
            const isins = existingComposition.fundsData.map((fund: any) => fund.fund.isin).filter((isin: string) => isin.trim() !== '');

            let compositionTableData: IPortfolioModelTable[] = existingComposition.fundsData.map((fund: any) =>
            ({
                nameFund: fund.fund.name,
                isin: fund.fund.isin,
                portfolioPercentage: fund.modelPercentage,
                fundPrice: 0,
                liquidationDate: '',
                currency: 'EUR',
                ironiaPoints: 0,
                ytdLinearReturn: undefined,
                oneYearVolatility: undefined,
                oneDayLinearReturn: 0
            }));

            // Si hay ISINs, obtener información adicional de los fondos
            if(isins.length > 0)
            {
                setIsLoadingCompositionData(true);
                try
                {
                    const additionalInfo: IPortfolioModelFullInformation[] = await FundService.getPortfolioModelFullInformation(isins);

                    // Mapear información adicional
                    const additionalInfoMap: {[isin: string]: IPortfolioModelFullInformation} = {};
                    additionalInfo.forEach((fundInfo: IPortfolioModelFullInformation) =>
                    {
                        additionalInfoMap[fundInfo.isin] = fundInfo;
                    });

                    // Combinar datos básicos con información adicional
                    compositionTableData = existingComposition.fundsData.map((fund: any) =>
                    {
                        const additionalData = additionalInfoMap[fund.fund.isin];
                        if(additionalData)
                        {
                            return{
                                nameFund: fund.fund.name,
                                isin: fund.fund.isin,
                                portfolioPercentage: fund.modelPercentage,
                                fundPrice: additionalData.vLiq,
                                liquidationDate: formatPortfolioModelDate(additionalData.dateVLiq),
                                currency: additionalData.currency,
                                ironiaPoints: additionalData.ironiaPoints,
                                ytdLinearReturn: additionalData.ytdLinearReturn * 100,
                                oneYearVolatility: additionalData.oneYearVolatility * 100,
                                oneDayLinearReturn: (additionalData.oneDayLinearReturn !== undefined && additionalData.oneDayLinearReturn !== null) ? additionalData.oneDayLinearReturn * 100 : 0
                            };
                        }
                        return{
                            nameFund: fund.fund.name,
                            isin: fund.fund.isin,
                            portfolioPercentage: fund.modelPercentage,
                            fundPrice: 0,
                            liquidationDate: '',
                            currency: 'EUR',
                            ironiaPoints: 0,
                            ytdLinearReturn: undefined,
                            oneYearVolatility: undefined,
                            oneDayLinearReturn: 0
                        };
                    });
                }
                catch(error)
                {
                    // Desactivar estado de carga en caso de error
                    setIsLoadingCompositionData(false);
                }
            }
            else
            {
                // Desactivar estado de carga si no hay ISINs
                setIsLoadingCompositionData(false);
            }

            // Guardar en caché para futuras consultas
            saveCompositionToSessionStorage(portfolioModelId, compositionId, compositionTableData);

            // Desactivar estado de carga
            setIsLoadingCompositionData(false);
            return compositionTableData;
        }

        // Si no se encuentra en los datos cargados, hacer llamada a la API
        try
        {
            const response = await portfolioModelWallets.getAllPortfolioModelHistoricByPortfolioModelId(parseInt(portfolioModelId));
            if(response.data && Array.isArray(response.data))
            {
                // Buscar la composición específica por ID
                const specificComposition = response.data.find((comp: any) => comp.compositionId === compositionId);

                if(specificComposition && specificComposition.fundsData)
                {
                    // Convertir los datos de la composición al formato de la tabla
                    const isins = specificComposition.fundsData.map((fund: any) => fund.fund.isin).filter((isin: string) => isin.trim() !== '');

                    let compositionTableData: IPortfolioModelTable[] = specificComposition.fundsData.map((fund: any) =>
                    ({
                        nameFund: fund.fund.name,
                        isin: fund.fund.isin,
                        portfolioPercentage: fund.modelPercentage,
                        fundPrice: 0,
                        liquidationDate: '',
                        currency: 'EUR',
                        ironiaPoints: 0,
                        ytdLinearReturn: undefined,
                        oneYearVolatility: undefined,
                        oneDayLinearReturn: 0
                    }));

                    // Si hay ISINs, obtener información adicional de los fondos
                    if(isins.length > 0)
                    {
                        try
                        {
                            const additionalInfo: IPortfolioModelFullInformation[] = await FundService.getPortfolioModelFullInformation(isins);

                            // Mapear información adicional
                            const additionalInfoMap: {[isin: string]: IPortfolioModelFullInformation} = {};
                            additionalInfo.forEach((fundInfo: IPortfolioModelFullInformation) =>
                            {
                                additionalInfoMap[fundInfo.isin] = fundInfo;
                            });

                            // Combinar datos básicos con información adicional
                            compositionTableData = specificComposition.fundsData.map((fund: any) =>
                            {
                                const additionalData = additionalInfoMap[fund.fund.isin];
                                if(additionalData)
                                {
                                    return{
                                        nameFund: fund.fund.name,
                                        isin: fund.fund.isin,
                                        portfolioPercentage: fund.modelPercentage,
                                        fundPrice: additionalData.vLiq,
                                        liquidationDate: formatPortfolioModelDate(additionalData.dateVLiq),
                                        currency: additionalData.currency,
                                        ironiaPoints: additionalData.ironiaPoints,
                                        ytdLinearReturn: additionalData.ytdLinearReturn * 100,
                                        oneYearVolatility: additionalData.oneYearVolatility * 100,
                                        oneDayLinearReturn: additionalData.oneDayLinearReturn !== undefined && additionalData.oneDayLinearReturn !== null ? additionalData.oneDayLinearReturn * 100 : 0
                                    };
                                }
                                
                                return{
                                    nameFund: fund.fund.name,
                                    isin: fund.fund.isin,
                                    portfolioPercentage: fund.modelPercentage,
                                    fundPrice: 0,
                                    liquidationDate: '',
                                    currency: 'EUR',
                                    ironiaPoints: 0,
                                    ytdLinearReturn: undefined,
                                    oneYearVolatility: undefined,
                                    oneDayLinearReturn: 0
                                };
                            });
                        }
                        catch(error)
                        {
                        }
                    }

                    // Guardar en caché
                    saveCompositionToSessionStorage(portfolioModelId, compositionId, compositionTableData);
                    setIsLoadingCompositionData(false);
                    return compositionTableData;
                }
            }
            // Desactivar estado de carga si no se encuentra la composición
            setIsLoadingCompositionData(false);
            return [];
        }
        catch(error)
        {
            // Desactivar estado de carga en caso de error
            setIsLoadingCompositionData(false);
            return [];
        }
    }, [getFromSessionStorage, getCompositionFromSessionStorage, saveCompositionToSessionStorage, compositionsData, mustRefresh]);

    //Zona de llamada a APIs

    /// Función para cargar datos de una cartera a través del portfolioModelId (tabla + gráfica)
    const loadPortfolioData = React.useCallback(async (portfolioId: string): Promise<CachedPortfolioData> =>
    {
        // Llamada principal para obtener datos básicos de tabla y gráfica
        const response = await portfolioModelWallets.getFullPortfolioModelInformation(parseInt(portfolioId));

        // Verificar si la respuesta indica que no hay datos disponibles
        // Solo lanzar error si no hay datos de tabla tampoco
        if((response.data.message === "NO_DATA_AVAILABLE" || response.data.Message === "NO_DATA_AVAILABLE") && response.data?.dataReportingPortfolio === null && (!response.data?.dataPortfolio?.fundItems || response.data.dataPortfolio.fundItems.length === 0))
        {
            throw new Error(`NO_DATA_AVAILABLE_${portfolioId}`);
        }

        const dataPortfolio = response.data?.dataPortfolio;
        const basicTableData = (dataPortfolio?.fundItems || []).map((fundItem: any) =>
        {
            const isin = fundItem.Fund?.ISIN || '';
            return{
                nameFund: fundItem.Fund?.Name || '',
                isin: isin,
                portfolioPercentage: Number(fundItem.ModelPercentage) || 0,
                fundPrice: 0,
                liquidationDate: '-',
                currency: 'EUR',
                ironiaPoints: 0,
                oneDayLinearReturn: 0,
                portfolioName: portfolioModelName,
                modelPortfolioId: parseInt(portfolioId)
            };
        });

        // Procesar datos de gráfica
        const series = response.data?.dataReportingPortfolio?.series;
        const reportingData: SerieData[] = series || [];
        const chartData = reportingData.map((item: SerieData) =>
        {
            const timestamp = new Date(item.date).getTime();
            return [timestamp, item.cartera];
        });

        const hasTableData = basicTableData.length > 0;
        const hasChartData = chartData.length > 0;
        const isNoChartDataOnly = hasTableData && !hasChartData;

        const ratiosData = response.data?.dataRatios;

        // Obtener ISINs únicos para la llamada adicional
        const isins = basicTableData.map((item: IPortfolioModelTable) => item.isin).filter((isin: string) => isin.trim() !== '');

        let finalTableData = basicTableData;

        // Si hay ISINs, obtener información adicional
        if(isins.length > 0)
        {
            try
            {
                const additionalInfo: IPortfolioModelFullInformation[] = await FundService.getPortfolioModelFullInformation(isins);

                // Mapear información adicional
                const additionalInfoMap: {[isin: string]: IPortfolioModelFullInformation} = {};
                additionalInfo.forEach((fundInfo: IPortfolioModelFullInformation) =>
                {
                    additionalInfoMap[fundInfo.isin] = fundInfo;
                });

                // Combinar datos básicos con información adicional
                finalTableData = basicTableData.map((item: IPortfolioModelTable) =>
                {
                    const additionalData = additionalInfoMap[item.isin];
                    if(additionalData)
                    {
                        return{
                            ...item,
                            fundPrice: additionalData.vLiq,
                            liquidationDate: formatPortfolioModelDate(additionalData.dateVLiq),
                            currency: additionalData.currency,
                            ironiaPoints: additionalData.ironiaPoints,
                            ytdLinearReturn: additionalData.ytdLinearReturn * 100,
                            oneYearVolatility: additionalData.oneYearVolatility * 100,
                            oneDayLinearReturn: (additionalData.oneDayLinearReturn !== undefined && additionalData.oneDayLinearReturn !== null) ? additionalData.oneDayLinearReturn * 100 : 0
                        };
                    }
                    return item;
                });
            }
            catch(error)
            {
            }
        }

        return{
            tableData: finalTableData,
            chartData,
            isNoChartDataOnly,
            ratiosData
        };
    }, [portfolioModelName]);

    /// Función para cargar las composiciones históricas de un portfolio model
    const loadPortfolioModelCompositions = React.useCallback(async (portfolioModelId: number): Promise<void> =>
    {
        try
        {
            setIsLoadingCompositions(true);
            const response = await portfolioModelWallets.getAllPortfolioModelHistoricByPortfolioModelId(portfolioModelId);

            if(response.data && Array.isArray(response.data))
            {
                // Obtener la fecha de hoy en formato dd/MM/yyyy
                const today = new Date();
                const todayFormatted = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

                // Verificar si ya existe una composición para hoy
                const hasTodayComposition = response.data.some((composition: IPortfolioModelHistoric) => composition.date === todayFormatted);

                // Crear composiciones con todos los datos (incluyendo fundsData)
                // FILTRAR composiciones que no tengan ID válido
                const validCompositions = response.data.filter((comp: any) => comp.compositionId !== undefined && comp.compositionId !== null);

                let compositionsWithVirtual: IPortfolioModelHistoric[] = validCompositions.map((comp: any) =>
                ({
                    compositionId: comp.compositionId,
                    date: comp.date,
                    portfolioModelId: comp.portfolioModelId,
                    fundsData: comp.fundsData || []
                }));

                // Si no existe composición para hoy, añadir una virtual
                if(!hasTodayComposition)
                {
                    // Intentar obtener datos de cartera modelo desde caché si no están en la ref
                    let currentFundsData: IFundData[] = [];
                    if(modelPortfolioCompositionRef.current.length > 0)
                    {
                        currentFundsData = convertPortfolioDataToFundData(modelPortfolioCompositionRef.current, location.state?.savingPlanId as number);
                    }
                    else
                    {
                        // Intentar obtener datos desde caché
                        const cachedData = getFromSessionStorage(portfolioModelId.toString());
                        if(cachedData && cachedData.tableData && cachedData.tableData.length > 0)
                        {
                            currentFundsData = convertPortfolioDataToFundData(cachedData.tableData, location.state?.savingPlanId as number);
                        }
                    }
                    
                    const virtualComposition: IPortfolioModelHistoric =
                    {
                        compositionId: -1,
                        date: todayFormatted,
                        portfolioModelId: portfolioModelId,
                        fundsData: currentFundsData
                    };

                    compositionsWithVirtual.push(virtualComposition);
                }

                // Ordenar las composiciones cronológicamente hacia atrás (hoy primero)
                const sortedCompositions = compositionsWithVirtual.sort((a: IPortfolioModelHistoric, b: IPortfolioModelHistoric) =>
                {
                    // Convertir fechas dd/MM/yyyy a objetos Date para ordenación correcta
                    const dateA = new Date(a.date.split('/').reverse().join('-')); // Convierte dd/MM/yyyy a yyyy-MM-dd
                    const dateB = new Date(b.date.split('/').reverse().join('-')); // Convierte dd/MM/yyyy a yyyy-MM-dd
                    return dateB.getTime() - dateA.getTime(); // Ordenación cronológica descendente (hoy primero)
                });

                // Almacenamos los datos completos de las composiciones ordenadas
                setCompositionsData(sortedCompositions);

                // Extraer las fechas de las composiciones ordenadas
                const dates = sortedCompositions.map((composition: IPortfolioModelHistoric) => composition.date);

                setAvailableDates(dates);
                setCounterValue(dates.length);
                
                // Solo resetear el índice si no es un refresh (para mantener la fecha actual del usuario)
                if(!mustRefresh)
                {
                    setCurrentDateIndex(0);
                }
            }
            else
            {
                // Si no hay datos, crear solo la composición virtual de hoy
                const today = new Date();
                const todayFormatted = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

                // Usar los datos ya cargados de modelPortfolioComposition si están disponibles
                const currentFundsData = modelPortfolioCompositionRef.current.length > 0 ? convertPortfolioDataToFundData(modelPortfolioCompositionRef.current, location.state?.savingPlanId as number) : [];

                const virtualComposition: IPortfolioModelHistoric =
                {
                    compositionId: -1,
                    date: todayFormatted,
                    portfolioModelId: portfolioModelId,
                    fundsData: currentFundsData
                };

                setCompositionsData([virtualComposition]);
                setAvailableDates([todayFormatted]);
                setCounterValue(1);
                
                // Solo resetear el índice si no es un refresh (para mantener la fecha actual del usuario)
                if(!mustRefresh)
                {
                    setCurrentDateIndex(0);
                }
            }
        }
        catch(error)
        {
            // En caso de error, crear solo la composición virtual de hoy
            const today = new Date();
            const todayFormatted = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

            // Usar los datos ya cargados de modelPortfolioComposition si están disponibles
            const currentFundsData = modelPortfolioCompositionRef.current.length > 0 ? convertPortfolioDataToFundData(modelPortfolioCompositionRef.current, location.state?.savingPlanId as number) : [];

            const virtualComposition: IPortfolioModelHistoric =
            {
                compositionId: -1,
                date: todayFormatted,
                portfolioModelId: portfolioModelId,
                fundsData: currentFundsData
            };

            setCompositionsData([virtualComposition]);
            setAvailableDates([todayFormatted]);
            setCounterValue(1);
            
            // Solo resetear el índice si no es un refresh (para mantener la fecha actual del usuario)
            if(!mustRefresh)
            {
                setCurrentDateIndex(0);
            }
        }
        finally
        {
            setIsLoadingCompositions(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.savingPlanId]);

    //Zona de cálculo de métricas

    const calculateMetrics = React.useCallback((chartData: any[]) =>
    {
        if(!chartData || chartData.length === 0)
        {
            return{
                fromStart: "0,00",
                lastSixMonths: "0,00",
                ytd: "0,00",
                year: "0,00"
            };
        }

        const sortedData = [...chartData].sort((a, b) => a[0] - b[0]);
        const firstValue = sortedData[0][1];
        const lastValue = sortedData[sortedData.length - 1][1];

        const fromStart = ((lastValue - firstValue) / Math.abs(firstValue)) * 100;
        const now = new Date();
        const currentYear = now.getFullYear();
        const startOfYear = new Date(currentYear, 0, 1).getTime();
        const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime();

        // YTD (desde inicio del año actual)
        const ytdStartData = sortedData.find(item => item[0] >= startOfYear);
        const ytdStart = ytdStartData ? ytdStartData[1] : firstValue;
        const ytd = ((lastValue - ytdStart) / Math.abs(ytdStart)) * 100;

        // Año (últimos 12 meses)
        const yearStartData = sortedData.find(item => item[0] >= oneYearAgo);
        const yearStart = yearStartData ? yearStartData[1] : firstValue;
        const year = ((lastValue - yearStart) / Math.abs(yearStart)) * 100;

        // Últimos 6 meses
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()).getTime();
        const sixMonthsStartData = sortedData.find(item => item[0] >= sixMonthsAgo);
        const sixMonthsStart = sixMonthsStartData ? sixMonthsStartData[1] : firstValue;
        const lastSixMonths = ((lastValue - sixMonthsStart) / Math.abs(sixMonthsStart)) * 100;

        const formatPercentage = (value: number) => value.toFixed(2).replace('.', ',');

        return{
            fromStart: formatPercentage(fromStart),
            lastSixMonths: formatPercentage(lastSixMonths),
            ytd: formatPercentage(ytd),
            year: formatPercentage(year)
        };
    }, []);

    /// Métricas calculadas dinámicamente
    const metrics = React.useMemo(() =>
    {
        // Solo calculamos métricas si hay exactamente una cartera seleccionada
        if(selectedOptions.length === 1)
        {   
            // Función helper para formatear valores del backend
            const formatBackendValue = (value: number | undefined | string): string | null =>
            {
                if(value === undefined || value === null)
                {
                    return null;
                }
                
                if(typeof value === 'string' && (value.toLowerCase() === 'nan' || value.trim() === ''))
                {
                    return null;
                }
                
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                
                // Validar NaN numérico
                if(isNaN(numValue) || !isFinite(numValue))
                {
                    return null;
                }
                
                // Los valores del backend vienen como decimales (0.05 = 5%)
                return (numValue * 100).toFixed(2).replace('.', ',');
            };

            // Intentar obtener los datos de ratiosData del backend (solo para 6m, ytd y 1y)
            let lastSixMonths: string | null = null;
            let ytd: string | null = null;
            let year: string | null = null;

            if(ratiosData && ratiosData.ratios && Array.isArray(ratiosData.ratios))
            {
                // Buscar el ratio de rentabilidad lineal
                const linearReturnRatio = ratiosData.ratios.find(ratio => ratio.ratioName === 'linear_return');
                
                if(linearReturnRatio && linearReturnRatio.values)
                {
                    // Intentar obtener valores del backend para cada período
                    lastSixMonths = formatBackendValue(linearReturnRatio.values['6m']);
                    ytd = formatBackendValue(linearReturnRatio.values['ytd']);
                    year = formatBackendValue(linearReturnRatio.values['1y']);
                }
            }

            // Calcular métricas manualmente (necesarias para fromStart y como fallback)
            const calculatedMetrics = series.length === 1 ? calculateMetrics(series[0].data) : null;

            return{
                fromStart: calculatedMetrics?.fromStart || "0,00",
                lastSixMonths: lastSixMonths || calculatedMetrics?.lastSixMonths || "0,00",
                ytd: ytd || calculatedMetrics?.ytd || "0,00",
                year: year || calculatedMetrics?.year || "0,00"
            };
        }

        // Valores por defecto cuando hay múltiples carteras o ninguna
        return{
            fromStart: "0,00",
            lastSixMonths: "0,00",
            ytd: "0,00",
            year: "0,00"
        };
    }, [selectedOptions.length, series, calculateMetrics, ratiosData]);

    // Función para actualizar la composición virtual con los datos actuales de cartera modelo
    const updateVirtualCompositionWithCurrentData = React.useCallback((portfolioId: string, tableData: IPortfolioModelTable[]) =>
    {
        if(tableData.length === 0)
        {
            return;
        }
        
        // Buscar la composición virtual en compositionsData
        setCompositionsData(prevCompositions =>
        {
            const updatedCompositions = prevCompositions.map(composition =>
            {
                if(composition.compositionId === -1 && composition.portfolioModelId === parseInt(portfolioId))
                {
                    // Convertir los datos de tabla a formato de fondos
                    const fundsData = convertPortfolioDataToFundData(tableData, location.state?.savingPlanId as number);
                    
                    return{
                        ...composition,
                        fundsData: fundsData
                    };
                }
                return composition;
            });
            
            return updatedCompositions;
        });
    }, [location.state?.savingPlanId]);

    // Función principal para procesar carteras seleccionadas
    const processSelectedPortfolios = React.useCallback(async (portfolioIds: string[]) =>
    {
        if(portfolioIds.length === 0)
        {
            setModelPortfolioComposition([]);
            setSeries([]);
            setNoDataAvailable(false);
            setNoChartDataOnly(false);
            return;
        }

        setIsLoading(true);
        setIsInterfaceDisabled(true);
        setNoDataAvailable(false);
        setNoChartDataOnly(false);

        try
        {
            const allTableData: IPortfolioModelTable[] = [];
            const allChartSeries: any[] = [];
            const colors = ['#00B050', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'];
            let hasAnyNoChartDataOnly = false;
            let allRatiosData: IDataRatios | null = null;

            for(let i = 0; i < portfolioIds.length; i++)
            {
                const portfolioId = portfolioIds[i];
                let portfolioData: CachedPortfolioData;

                // Verificar caché en sessionStorage
                const cachedData = getFromSessionStorage(portfolioId);
                if(cachedData)
                {
                    // Verificar si los datos cacheados representan "sin datos"
                    const hasTableData = cachedData.tableData.length > 0;
                    const hasChartData = cachedData.chartData.length > 0;

                    // Solo considera "sin datos" cuando no hay datos de tabla
                    if(!hasTableData)
                    {
                        // Si solo hay una cartera seleccionada, mostrar el mensaje de error
                        if(portfolioIds.length === 1)
                        {
                            setNoDataAvailable(true);
                            setModelPortfolioComposition([]);
                            setSeries([]);
                            return;
                        }
                        else
                        {
                            // Si hay múltiples carteras, simplemente continuar con las otras
                            continue;
                        }
                    }

                    // Si hay datos de tabla pero no de gráfica, marcar como solo sin gráfica
                    if(hasTableData && !hasChartData)
                    {
                        hasAnyNoChartDataOnly = true;
                    }

                    portfolioData = cachedData;
                }
                else
                {
                    try
                    {
                        portfolioData = await loadPortfolioData(portfolioId);

                        // Guardar en caché
                        saveToSessionStorage(portfolioId, portfolioData);
                    }
                    catch(loadError: any)
                    {
                        // Verificar si es un error de "sin datos disponibles"
                        if(loadError.message && loadError.message.startsWith('NO_DATA_AVAILABLE'))
                        {
                            // Si solo hay una cartera seleccionada, mostrar el mensaje de error
                            if(portfolioIds.length === 1)
                            {
                                setNoDataAvailable(true);
                                setModelPortfolioComposition([]);
                                setSeries([]);
                                return;
                            }
                            else
                            {
                                // Si hay múltiples carteras, simplemente continuar con las otras
                                continue;
                            }
                        }
                        else
                        {
                            // Re-lanzar otros tipos de errores
                            throw loadError;
                        }
                    }
                }

                // Verificar si esta cartera tiene datos de tabla pero no de gráfica
                if(portfolioData.isNoChartDataOnly)
                {
                    hasAnyNoChartDataOnly = true;
                }

                // Actualizar nombres de cartera si es necesario
                const updatedTableData = portfolioData.tableData.map((item: IPortfolioModelTable) =>
                ({
                    ...item,
                    portfolioName: portfolioIds.length > 1 ? options.find(opt => opt.key === portfolioId)?.text : undefined
                }));

                // Agregar datos de tabla
                allTableData.push(...updatedTableData);

                // Agregar datos de gráfica
                if(portfolioData.chartData.length > 0)
                {
                    const portfolioName = options.find(opt => opt.key === portfolioId)?.text || `Cartera ${portfolioId}`;
                    allChartSeries.push(
                    {
                        name: portfolioName,
                        data: portfolioData.chartData,
                        color: colors[i % colors.length]
                    });
                }

                // Agregar datos de ratios (solo para la primera cartera si hay múltiples)
                if(portfolioData.ratiosData && !allRatiosData)
                {
                    allRatiosData = portfolioData.ratiosData;
                }
            }

            // Actualizar estados solo cuando todo esté listo 
            setModelPortfolioComposition(allTableData);
            setSeries(allChartSeries);
            setRatiosData(allRatiosData);
            
            // Actualizar la composición virtual con los datos recién cargados
            updateVirtualCompositionWithCurrentData(portfolioIds[0], allTableData);

            // Si solo hay una cartera y no tiene datos de gráfica pero sí de tabla
            if(portfolioIds.length === 1 && hasAnyNoChartDataOnly)
            {
                setNoChartDataOnly(true);
            }
        }
        catch(error)
        {
        }
        finally
        {
            setIsLoading(false);
            setIsInterfaceDisabled(false);
        }
    }, [getFromSessionStorage, loadPortfolioData, saveToSessionStorage, options, updateVirtualCompositionWithCurrentData]);

    // Manejar cambios en la selección
    const onSelectionChange = React.useCallback((event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void =>
    {
        if(item && !isInterfaceDisabled)
        {
            const newSelectedOptions = item.selected ? [...selectedOptions, item.key as string] : selectedOptions.filter(key => key !== item.key);
            setSelectedOptions(newSelectedOptions);
        }
    }, [selectedOptions, isInterfaceDisabled]);

    // Funciones auxiliares
    const getSelectedPortfolioNames = () =>
    {
        return selectedOptions.map(key => options.find(opt => opt.key === key)?.text).filter(text => text !== undefined).join(', ');
    };

    const getHeaderTitle = () =>
    {
        if(selectedOptions.length === 0)
        {
            return 'Resumen de Cartera Modelo';
        }

        if(selectedOptions.length === 1)
        {
            return `Resumen de Cartera Modelo - ${portfolioModelName || ''}`;
        }

        return `Resumen de Carteras Modelo - ${selectedOptions.length} seleccionadas`;
    };

    // Función para manejar el click del botón Aportación Periódica
    const handlePeriodicContribution = React.useCallback(() =>
    {
        if(selectedOptions.length === 1 && location.state?.portfolioModelId && selectedOptions[0] === location.state.portfolioModelId.toString())
        {
            const portfolioModelId = location.state.portfolioModelId;
            const portfolioModelName = location.state.portfolioModelName;
            const savingPlanId = location.state.savingPlanId;
            const contract = location.state.contract;

            history.push(`/ModelPortfolio/PeriodicContributions/${portfolioModelId}`,
            {
                portfolioModelId,
                portfolioModelName,
                savingPlanId,
                contract
            });
        }
    }, [selectedOptions, location.state, history]);

    // Funciones para navegación de fechas
    const handlePreviousDate = React.useCallback(() =>
    {
        // Con ordenación descendente (hoy primero), "anterior" significa ir a fechas más antiguas (índice mayor)
        if(currentDateIndex < availableDates.length - 1 && availableDates.length > 0)
        {
            setCurrentDateIndex(prev => prev + 1);
        }
    }, [currentDateIndex, availableDates.length]);

    const handleNextDate = React.useCallback(() =>
    {
        // Con ordenación descendente (hoy primero), "siguiente" significa ir a fechas más recientes (índice menor)
        if(currentDateIndex > 0 && availableDates.length > 0)
        {
            setCurrentDateIndex(prev => prev - 1);
        }
    }, [currentDateIndex, availableDates.length]);

    const currentDate = React.useMemo(() => 
    {
        if(availableDates.length === 0)
        {
            return '';
        }
        return availableDates[currentDateIndex] || availableDates[0];
    }, [availableDates, currentDateIndex]);

    // Calcular si la composición seleccionada es la del día de hoy
    const isLatestComposition = React.useMemo(() => 
    {
        if(!currentDate || availableDates.length === 0)
        {
            return false;
        }

        // Obtener la fecha de hoy en formato dd/MM/yyyy
        const today = new Date();
        const todayFormatted = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

        // Verificar si la fecha actual seleccionada es la de hoy
        const isLatest = currentDate === todayFormatted;
        return isLatest;
    }, [currentDate, availableDates]);

    // Obtener los datos de la composición actual seleccionada
    const currentCompositionData = React.useMemo(() => 
    {
        if(!currentDate || compositionsData.length === 0)
        {
            return null;
        }

        const composition = compositionsData.find(comp => comp.date === currentDate);
        if(!composition)
        {
            return null;
        }

        try
        {
            const fundsData: IFundData[] = composition.fundsData;
            const funds = Array.isArray(fundsData) ? fundsData.map((fundData: IFundData) =>
            {
                return{
                    name: fundData.fund.name || '',
                    isin: fundData.fund.isin || '',
                    weight: fundData.modelPercentage || 0,
                    finametrixId: fundData.fund.finametrixId || '',
                    allFundsId: fundData.fund.allFundsId || 0
                };
            }) : [];

            return{
                date: composition.date,
                compositionId: composition.compositionId,
                funds: funds
            };
        }
        catch(error)
        {
            console.error('Error parsing funds data:', error);
            return{
                date: composition.date,
                compositionId: composition.compositionId,
                funds: []
            };
        }
    }, [currentDate, compositionsData]);

    // Configuración de botones personalizados para el rangeSelector
    // Solo para Portfolio Model - incluye intervalos adicionales
    // 1y siempre aparece (aunque no haya ratios), 2y y 3y solo si hay datos completos
    const customRangeButtons: CustomRangeButton[] = React.useMemo(() =>
    {
        return [
            {
                type: 'month',
                count: 9,
                text: '9m'
            },
            {
                type: 'year',
                count: 1,
                text: '1y'
            },
            {
                type: 'year',
                count: 2,
                text: '2y',
                minDataPoints: 730 // 2 años completos de datos
            },
            {
                type: 'year',
                count: 3,
                text: '3y',
                minDataPoints: 1095 // 3 años completos de datos
            }
        ];
    }, []);

    // OPTIMIZACIÓN: Memoizar las props del componente Highstock para evitar re-renders innecesarios
    // Solo se recalcula cuando cambian los datos del gráfico (series) o customOptions
    const highstockProps = React.useMemo(() =>
    {
        return{
            series,
            customOptions,
            graphicType: GraphicType.linearGraphic,
            onChange: handlePeriodChange,
            customButtons: customRangeButtons
        };
    }, [series, customOptions, handlePeriodChange, customRangeButtons]);

    //Zona de manejo de modales

    const handleEdit = React.useCallback(() =>
    {
        setManageCompositionMode('edit');
        setIsManageCompositionModalOpen(true);
    }, []);

    const handleDelete = React.useCallback(() =>
    {
        if(currentCompositionData)
        {
            setIsDeleteModalOpen(true);
        }
    }, [currentCompositionData]);

    // Funciones para manejar el modal de eliminación
    const handleDeleteModalClose = React.useCallback(() =>
    {
        setIsDeleteModalOpen(false);
    }, []);

    const handleDeleteConfirm = React.useCallback(async () =>
    {
        if(!currentCompositionData || !currentDate)
        {
            return;
        }

        try
        {
            setIsDeletingComposition(true);

            // Obtener el compositionId de la composición actual
            const currentComposition = compositionsData.find(comp => comp.date === currentDate);
            if(!currentComposition || !currentComposition.compositionId)
            {
                return;
            }

            // Obtener el portfolioModelId
            if(selectedOptions.length !== 1)
            {
                return;
            }

            const portfolioModelId = parseInt(selectedOptions[0]);
            const compositionId = currentComposition.compositionId;

            const deleteCompositionData =
            {
                portfolioModelId: portfolioModelId,
                compositionId: compositionId,
                composition:
                [
                    {
                        date: currentCompositionData.date,
                        isinAndWeight: currentCompositionData.funds.reduce((acc, fund) => 
                        {
                            acc[fund.isin] = fund.weight;
                            return acc;
                        }, {} as {[key: string]: number})
                    }
                ]
            };

            // Realizar la llamada al endpoint de eliminación
            const result = await portfolioModelWallets.deleteComposition(deleteCompositionData);

            if(result.status)
            {
                // Cerrar el modal
                setIsDeleteModalOpen(false);

                // Limpiar el caché del portfolio para forzar recarga de datos de la gráfica
                sessionStorage.removeItem(getStorageKey(portfolioModelId.toString()));
                sessionStorage.removeItem(getChartStorageKey(portfolioModelId.toString()));
                sessionStorage.removeItem(getRatiosStorageKey(portfolioModelId.toString()));

                // Limpiar las referencias de carga para forzar recarga
                loadedPortfolioIdsRef.current.clear();
                loadedCompositionsRef.current.clear();

                // Activar el semáforo para forzar refresco completo
                // Esto disparará los useEffect que harán las recargas necesarias
                setMustRefresh(true);
            }
        }
        catch(error)
        {
            console.error('Error deleting composition:', error);
        }
        finally
        {
            setIsDeletingComposition(false);
        }
    }, [currentCompositionData, currentDate, selectedOptions, compositionsData]);

    const handleAddComposition = React.useCallback(() =>
    {
        setManageCompositionMode('add');
        setIsManageCompositionModalOpen(true);
    }, []);

    const handleImportComposition = React.useCallback(() =>
    {
        setIsImportModalOpen(true);
    }, []);

    const handleImportModalClose = React.useCallback(() =>
    {
        setIsImportModalOpen(false);
    }, []);

    const handleImportData = React.useCallback(async (shouldReloadPortfolioModel?: boolean) =>
    {
        setIsImportModalOpen(false);

        // Siempre limpiar caché de composiciones porque se han importado nuevas
        loadedPortfolioIdsRef.current.clear();
        loadedCompositionsRef.current.clear();

        // Si userRequestPortfolioModelChange estaba activado, también limpiar caché de cartera modelo
        if(shouldReloadPortfolioModel)
        {
            // Limpiar caché de cartera modelo
            sessionStorage.removeItem(getStorageKey(selectedOptions[0]));
            sessionStorage.removeItem(getChartStorageKey(selectedOptions[0]));
            sessionStorage.removeItem(getRatiosStorageKey(selectedOptions[0]));
        }

        // Activar refresh
        setMustRefresh(true);
    }, [selectedOptions]);

    const handleManageCompositionClose = React.useCallback(() =>
    {
        setIsManageCompositionModalOpen(false);
        
        // Resetear la fecha SOLO cuando se cierra el modal
        setCurrentDateIndex(0);
    }, []);

    const handleManageCompositionSave = React.useCallback(async (compositionData: any) =>
    {
        if(!compositionData || !compositionData.portfolioModelId)
        {
            console.error('Datos de composición inválidos:', compositionData);
            return;
        }

        try
        {
            setIsInterfaceDisabled(true);

            if(compositionData.mode === 'add')
            {
                // Activar estado de carga para el modal
                setIsManagingComposition(true);

                // Lógica para añadir composición simple
                if(!compositionData.isValid)
                {
                    console.error('La composición no es válida. El total debe ser 100%');
                    setIsManagingComposition(false);
                    return;
                }

                // Convertir la fecha del formato YYYY-MM-DD a DD/MM/YYYY
                const dateObj = new Date(compositionData.date);
                const formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;

                // Construir el objeto isinAndWeight
                const isinAndWeight: {[key: string]: number} = {};
                compositionData.funds.forEach((fund: any) =>
                {
                    isinAndWeight[fund.isin] = fund.percentage;
                });

                // Construir el body según la interfaz CompositionFromBody
                const createCompositionBody =
                {
                    portfolioModelId: compositionData.portfolioModelId,
                    isImportation: false, // Es una composición simple, no una importación
                    elementsUsed: null, // No aplica para composiciones simples
                    composition:
                    [
                        {
                            date: formattedDate,
                            isinAndWeight: isinAndWeight
                        }
                    ]
                };

                // Realizar la llamada al endpoint
                const result = await portfolioModelWallets.createCompositions(createCompositionBody);

                if(result.status)
                {
                    // Limpiar las referencias de carga para forzar recarga
                    loadedPortfolioIdsRef.current.clear();
                    loadedCompositionsRef.current.clear();

                    // Cerrar el modal primero para evitar cambios visuales durante el proceso
                    setIsManageCompositionModalOpen(false);

                    // Limpiar el caché del portfolio para forzar recarga de datos de la gráfica
                    sessionStorage.removeItem(getStorageKey(compositionData.portfolioModelId.toString()));
                    sessionStorage.removeItem(getChartStorageKey(compositionData.portfolioModelId.toString()));
                    sessionStorage.removeItem(getRatiosStorageKey(compositionData.portfolioModelId.toString()));

                    // Activar el semáforo para forzar refresco completo (después de cerrar el modal)
                    setMustRefresh(true);
                }
            }
            else if(compositionData.mode === 'edit')
            {
                // Activar estado de carga para el modal
                setIsManagingComposition(true);

                // Lógica para editar composición existente
                if(!compositionData.isValid)
                {
                    console.error('La composición no es válida. El total debe ser 100%');
                    setIsManagingComposition(false);
                    return;
                }

                // Verificamos si estamos editando el del día de hoy
                const isVirtualComposition = isLatestComposition && manageCompositionMode === 'edit';

                if(isVirtualComposition)
                {
                    // Editar la cartera modelo virtual usando updateModelWallet
                    const savingPlanId = location.state?.savingPlanId;
                    if(!savingPlanId)
                    {
                        setIsManagingComposition(false);
                        return;
                    }

                    // Construir DataAndCartLines[] a partir de los datos del modal y la composición actual
                    const cartLines: DataAndCartLines[] = compositionData.funds.map((fund: any) =>
                    {
                        // Buscar el fondo correspondiente en modelPortfolioComposition para obtener datos completos
                        const existingFund = modelPortfolioComposition.find(f => f.isin === fund.isin);

                        const cartLine: CartLinesFromDB =
                        {
                            name: existingFund?.nameFund || fund.name || '',
                            isin: fund.isin,
                            weight: `${fund.percentage.toFixed(2)} %`,
                            allFundsId: 0 //Vamos a buscarlo en el backend
                        };

                        return{
                            fund: cartLine,
                            savingsPlanId: savingPlanId,
                            readOnly: false,
                            modelPercentage: fund.percentage
                        };
                    });

                    await portfolioModelWallets.updateModelWallet(portfolioModelName || '', compositionData.portfolioModelId, cartLines);

                    // Limpiar el caché específico de la composición virtual (del día de hoy)
                    const currentCompositionId = currentCompositionData?.compositionId;
                    if(currentCompositionId)
                    {
                        clearCompositionFromSessionStorage(compositionData.portfolioModelId.toString(), currentCompositionId);
                    }

                    // Limpiar también el caché del portfolio general ya que updateModelWallet puede cambiar la estructura
                    const portfolioId = compositionData.portfolioModelId.toString();
                    try
                    {
                        sessionStorage.removeItem(getStorageKey(portfolioId));
                        sessionStorage.removeItem(getChartStorageKey(portfolioId));
                        sessionStorage.removeItem(getRatiosStorageKey(portfolioId));
                    }
                    catch(error)
                    {
                    }

                    // Limpiar las referencias de carga para forzar recarga
                    loadedPortfolioIdsRef.current.clear();
                    loadedCompositionsRef.current.clear();

                    // Activar el semáforo para forzar refresco completo
                    setMustRefresh(true);

                    // Cerrar el modal después de procesar la respuesta exitosa
                    setIsManageCompositionModalOpen(false);
                }
                else
                {
                    // Zona de Edición de las composiciones históricas
                    const formattedDate = currentCompositionData?.date || '';
                    const currentCompositionId = currentCompositionData?.compositionId;

                    if(!formattedDate || !currentCompositionId)
                    {
                        setIsManagingComposition(false);
                        return;
                    }

                    const isinAndWeight: { [key: string]: number } = {};
                    compositionData.funds.forEach((fund: any) =>
                    {
                        isinAndWeight[fund.isin] = fund.percentage;
                    });

                    const createCompositionBody =
                    {
                        portfolioModelId: compositionData.portfolioModelId,
                        isImportation: false, // Es una composición simple, no una importación
                        elementsUsed: null, // No aplica para composiciones simples
                        composition:
                        [
                            {
                                date: formattedDate, // Usamos la fecha existente de la composición
                                isinAndWeight: isinAndWeight
                            }
                        ]
                    };

                    // Realizar la llamada al endpoint createCompositions
                    const result = await portfolioModelWallets.createCompositions(createCompositionBody);

                    if(result.status)
                    {
                        // Cerrar el modal primero para evitar cambios visuales durante el proceso
                        setIsManageCompositionModalOpen(false);

                        // Limpiar el caché específico de la composición editada
                        clearCompositionFromSessionStorage(compositionData.portfolioModelId.toString(), currentCompositionId);

                        // Limpiar el caché del portfolio para forzar recarga de datos de la gráfica
                        sessionStorage.removeItem(getStorageKey(compositionData.portfolioModelId.toString()));
                        sessionStorage.removeItem(getChartStorageKey(compositionData.portfolioModelId.toString()));
                        sessionStorage.removeItem(getRatiosStorageKey(compositionData.portfolioModelId.toString()));

                        // Limpiar las referencias de carga para forzar recarga
                        loadedPortfolioIdsRef.current.clear();
                        loadedCompositionsRef.current.clear();

                        // Activar el semáforo para forzar refresco completo (después de cerrar el modal)
                        setMustRefresh(true);
                    }
                }
            }
        }
        catch(error)
        {
        }
        finally
        {
            setIsInterfaceDisabled(false);
            setIsManagingComposition(false);
        }
    }, [currentCompositionData, clearCompositionFromSessionStorage, isLatestComposition, location.state?.savingPlanId, manageCompositionMode, modelPortfolioComposition, portfolioModelName, loadedPortfolioIdsRef, loadedCompositionsRef, setIsManageCompositionModalOpen, setMustRefresh, setIsManagingComposition, setIsInterfaceDisabled]);

    // Cargamos datos cuando cambien las opciones seleccionadas evitando re-renders innecesarios
    React.useEffect(() =>
    {
        if(selectedOptions.length === 0)
        {
            setNoDataAvailable(false);
            setNoChartDataOnly(false);
            setModelPortfolioComposition([]);
            setSeries([]);
            return;
        }

        // Para 1 portfolio, usamos la ref y evitamos recargas innecesarias
        if(selectedOptions.length === 1)
        {
            const portfolioId = selectedOptions[0];

            // Si venimos de múltiples carteras, limpiar el ref para recalcular
            if(loadedPortfolioIdsRef.current.size > 1)
            {
                loadedPortfolioIdsRef.current.clear();
            }

            // Si no hemos cargado este portfolio, procesarlo
            if(!loadedPortfolioIdsRef.current.has(portfolioId))
            {
                setNoDataAvailable(false);
                setNoChartDataOnly(false);
                processSelectedPortfolios([portfolioId]).then(() =>
                {
                    loadedPortfolioIdsRef.current.add(portfolioId);
                });
            }
            return;
        }

        // Para múltiples portfolios, siempre pasamos toda la lista
        // porque necesitamos combinarlos en la tabla/gráfica
        setNoDataAvailable(false);
        setNoChartDataOnly(false);

        // Limpiar datos anteriores para recalcular con múltiples carteras
        setModelPortfolioComposition([]);
        setSeries([]);

        processSelectedPortfolios(selectedOptions).then(() =>
        {
            selectedOptions.forEach(id => loadedPortfolioIdsRef.current.add(id));
        });

    }, [selectedOptions, processSelectedPortfolios]);

    // Cargar composiciones históricas solo cuando hay exactamente 1 portfolio model seleccionado
    React.useEffect(() =>
    {
        // Si no hay exactamente 1 selección, limpiamos
        if(selectedOptions.length !== 1)
        {
            setCompositionsData([]);
            setAvailableDates([]);
            setCounterValue(0);
            setCurrentDateIndex(0);

            // Limpiar el ref para permitir recarga cuando volvamos a 1 selección
            loadedCompositionsRef.current.clear();
            return;
        }

        const portfolioModelId = parseInt(selectedOptions[0]);

        // Comprobamos si ya hemos cargado esta composición (a menos que mustRefresh esté activo)
        if(!mustRefresh && loadedCompositionsRef.current.has(portfolioModelId))
        {
            return;
        }

        loadPortfolioModelCompositions(portfolioModelId).then(() =>
        {
            // Marcamos este portfolio como cargado para futuras referencias
            loadedCompositionsRef.current.add(portfolioModelId);

            // Si mustRefresh está activo, también recargar los datos del portfolio
            if(mustRefresh)
            {
                const portfolioId = selectedOptions[0];
                processSelectedPortfolios([portfolioId]).then(() =>
                {
                    loadedPortfolioIdsRef.current.add(portfolioId);
                    // Desactivar el semáforo después del refresco completo
                    setMustRefresh(false);
                });
            }
        })
        .catch(error =>
        {
            console.error('Error cargando composiciones históricas:', error);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions, mustRefresh, processSelectedPortfolios]);

    // Actualizar tabla cuando cambie la composición seleccionada
    React.useEffect(() =>
    {
        // Solo procesar si hay exactamente 1 portfolio seleccionado y hay composiciones cargadas
        if(selectedOptions.length !== 1 || compositionsData.length === 0)
        {
            return;
        }

        // Si mustRefresh está activo, esperar a que se complete la recarga del portfolio
        if(mustRefresh)
        {
            return;
        }

        const portfolioModelId = selectedOptions[0];
        const currentComposition = compositionsData[currentDateIndex];

        if(!currentComposition || currentComposition.compositionId === undefined)
        {
            return;
        }

        // Creamos una clave única para esta composición para evitar llamadas duplicadas.
        const compositionKey = `${portfolioModelId}_${currentComposition.compositionId}`;
        setIsLoadingCompositionData(true);

        // Evitar llamadas duplicadas si ya está cargando la misma composición
        if(currentlyLoadingCompositionsRef.current.has(compositionKey))
        {
            // Esperar a que se complete la carga en progreso y luego desactivar el estado
            const checkLoadingComplete = () =>
            {
                if(!currentlyLoadingCompositionsRef.current.has(compositionKey))
                {
                    setIsLoadingCompositionData(false);
                }
                else
                {
                    setTimeout(checkLoadingComplete, 100);
                }
            };
            checkLoadingComplete();
            return;
        }

        // Marcamos esta composición como en carga
        currentlyLoadingCompositionsRef.current.add(compositionKey);

        // Activar estado de carga para la tabla
        setIsLoadingCompositionData(true);

        // Cargar datos de la composición seleccionada
        loadCompositionData(portfolioModelId, currentComposition.compositionId).then((compositionTableData) =>
        {
            setModelPortfolioComposition(compositionTableData);
            // Desactivar estado de carga
            setIsLoadingCompositionData(false);
            // Elminar de la lista de cargas en progreso
            currentlyLoadingCompositionsRef.current.delete(compositionKey);
        })
        .catch((error) =>
        {
            console.error('❌ [useEffect-composition] Error loading composition data:', error);
            // Desactivar estado de carga incluso en caso de error
            setIsLoadingCompositionData(false);
            // Eliminamos de la lista de cargas en progreso incluso en caso de error
            currentlyLoadingCompositionsRef.current.delete(compositionKey);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions, compositionsData, currentDateIndex, mustRefresh]);

    return(
        <DocumentTitle title="IronIA - Resumen de Cartera Modelo">
            <React.Fragment>
                <HeaderContainer className="header">
                    <h2>
                        {getHeaderTitle()}
                    </h2>
                </HeaderContainer>
                <Container theme={theme}>
                    <Label theme={theme}>
                        {location.state?.savingPlanName ? `${location.state.savingPlanName}` : ''}
                    </Label>
                    <StyledDropdown key={`dropdown-${selectedOptions.length}`} multiSelect options={options} selectedKeys={selectedOptions}
                        onChange={onSelectionChange}
                        placeholder=
                        {
                            isInterfaceDisabled || isLoading
                                ? "Procesando datos..."
                                : "Selecciona una o más carteras Modelo"
                        }
                        disabled={isInterfaceDisabled || isLoading}
                        theme={theme}
                    />
                    {selectedOptions.length === 1 && location.state?.portfolioModelId && selectedOptions[0] === location.state.portfolioModelId.toString() &&
                    (
                        <ActionButton disabled={isInterfaceDisabled || isLoading} onClick={handlePeriodicContribution} theme={theme}>
                            {isInterfaceDisabled || isLoading ? "Procesando..." : "Aportación Periódica"}
                        </ActionButton>
                    )}
                    {selectedOptions.length === 1 &&
                    (
                        <React.Fragment>
                            <CounterLabel theme={theme} title="Número de Composiciones de esta Cartera Modelo">
                                {isLoadingCompositions ? '...' : counterValue}
                            </CounterLabel>
                            <DateNavigator theme={theme}>
                                <NavigationArrow onClick={handlePreviousDate}
                                    disabled={isInterfaceDisabled || isLoading || isLoadingCompositions || currentDateIndex === availableDates.length - 1 || availableDates.length === 0}
                                    theme={theme}
                                >
                                    ‹
                                </NavigationArrow>
                                <DateLabel theme={theme}>
                                    {isLoadingCompositions ? 'Cargando...' : availableDates.length === 0 ? 'Sin fechas' : currentDate}
                                </DateLabel>
                                <NavigationArrow onClick={handleNextDate}
                                    disabled={isInterfaceDisabled || isLoading || isLoadingCompositions || currentDateIndex === 0 || availableDates.length === 0}
                                    theme={theme}
                                >
                                    ›
                                </NavigationArrow>
                            </DateNavigator>
                            <EditButton onClick={handleEdit} disabled={isInterfaceDisabled || isLoading} title="Editar" theme={theme}>
                                <Icon iconName="Edit" />
                            </EditButton>
                            {!isLatestComposition &&
                            (
                                <DeleteButton onClick={handleDelete} disabled={isInterfaceDisabled || isLoading} title="Eliminar" theme={theme}>
                                    <Icon iconName="Delete" />
                                </DeleteButton>
                            )}
                            <MainButton onClick={handleAddComposition} disabled={isInterfaceDisabled || isLoading} theme={theme}>
                                Añadir Composición
                            </MainButton>
                            <TooltipHost content="Importar composiciones desde archivo CSV o Excel" delay={0}
                                styles={{root: {display: 'inline-block'}}}
                            >
                                <ExportIcon onClick={isInterfaceDisabled || isLoading ? undefined : handleImportComposition} className={isInterfaceDisabled || isLoading ? 'disabled' : ''} theme={theme}>
                                    <ExcelLogoIcon />
                                </ExportIcon>
                            </TooltipHost>
                        </React.Fragment>
                    )}
                </Container>
                <CardsContainer>
                    <Card className="portfolio-card">
                        <CardTitle>
                            {selectedOptions.length > 1 ? 'Carteras Modelo Seleccionadas' : 'Cartera Modelo Seleccionada'}
                        </CardTitle>
                        <CardData>
                            {selectedOptions.length > 0 ? getSelectedPortfolioNames() : 'No seleccionada'}
                        </CardData>
                    </Card>
                    <MetricsContainer>
                        <Card className="metric-card">
                            <CardTitle>Desde inicio</CardTitle>
                            <MetricCardData>
                                {metrics.fromStart < "0,00" && <DecrementIcon />}
                                {metrics.fromStart > "0,00" && <IncrementIcon />}
                                <span>{metrics.fromStart}%</span>
                            </MetricCardData>
                        </Card>
                        <Card className="metric-card">
                            <CardTitle>YTD</CardTitle>
                            <MetricCardData>
                                {metrics.ytd < "0,00" && <DecrementIcon />}
                                {metrics.ytd > "0,00" && <IncrementIcon />}
                                <span>{metrics.ytd}%</span>
                            </MetricCardData>
                        </Card>
                        <Card className="metric-card">
                            <CardTitle>6 meses</CardTitle>
                            <MetricCardData>
                                {metrics.lastSixMonths < "0,00" && <DecrementIcon />}
                                {metrics.lastSixMonths > "0,00" && <IncrementIcon />}
                                <span>{metrics.lastSixMonths}%</span>
                            </MetricCardData>
                        </Card>
                        <Card className="metric-card">
                            <CardTitle>Año</CardTitle>
                            <MetricCardData>
                                {metrics.year < "0,00" && <DecrementIcon />}
                                {metrics.year > "0,00" && <IncrementIcon />}
                                <span>{metrics.year}%</span>
                            </MetricCardData>
                        </Card>
                    </MetricsContainer>
                </CardsContainer>
                <GraphicsContainer>
                    <GraphicsContentContainer>
                        <RatiosSectionContainer>
                            <RatioTablePortfolioModel ratiosData={ratiosData} selectedPeriod={selectedPeriod} isLoading={isLoading} />
                        </RatiosSectionContainer>
                        <ChartSectionContainer>
                            <GraphicTitle>
                                Rentabilidad de tu cartera (TWR)
                            </GraphicTitle>
                            <LoadingGraphicComponent isLoading={isLoading} noDataAvailable={noDataAvailable} noChartDataOnly={noChartDataOnly} selectedOptions={selectedOptions} />
                            {!isLoading && !(noDataAvailable && selectedOptions.length === 1) && !(noChartDataOnly && selectedOptions.length === 1) &&
                            (
                                // OPTIMIZACIÓN: Usar componente memoizado con props optimizadas
                                // Solo se re-renderiza cuando cambian los datos del gráfico (series)
                                <MemoizedHighstock {...highstockProps} />
                            )}
                        </ChartSectionContainer>
                    </GraphicsContentContainer>
                </GraphicsContainer>
                <PortfolioModelTable isLatestComposition={isLatestComposition} data={modelPortfolioComposition} isLoading={isLoading || isLoadingCompositionData} showPortfolioColumn={selectedOptions.length > 1} currentDate={currentDate} />
                <ImportComposition isOpen={isImportModalOpen} onClose={handleImportModalClose}
                    onImport={handleImportData} userId={userId}
                    portfolioModelName={portfolioModelName}
                    portfolioModelId={selectedOptions.length === 1 ? parseInt(selectedOptions[0]) : undefined}
                    existingCompositions={compositionsData}
                />
                <ManageComposition isOpen={isManageCompositionModalOpen} onClose={handleManageCompositionClose} onSave={handleManageCompositionSave}
                    mode={manageCompositionMode} portfolioModelName={portfolioModelName}
                    portfolioModelId={selectedOptions.length === 1 ? parseInt(selectedOptions[0]) : undefined}
                    isVirtualComposition={isLatestComposition && manageCompositionMode === 'edit'}
                    isLoading={isManagingComposition}
                    currentCompositionData={manageCompositionMode === 'edit' ? currentCompositionData : null}
                    existingCompositions={compositionsData}
                />
                {!isLatestComposition &&
                (
                    <DeleteCompositionModal isOpen={isDeleteModalOpen} onClose={handleDeleteModalClose} onConfirm={handleDeleteConfirm}
                        compositionData={currentCompositionData}
                        isLoading={isDeletingComposition}
                    />
                )}
            </React.Fragment>
        </DocumentTitle>
    );
}

export default SummaryPortfolioModel;